import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EventType, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthenticationDomainService } from './modules/authentication/services/authentication-domain.service';
import { SpinnerComponent } from './shared/components/spinner/spinner.component';
import { SwUpdate } from '@angular/service-worker';
import { MatDialog } from '@angular/material/dialog';
import { UpdateAvailableComponent } from 'src/app/shared/components/update-available/update-available.component';
import * as R from 'remeda';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SpinnerComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  protected readonly autoscroll$ = this.router.events.pipe(
    takeUntilDestroyed(),
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map((e) => this.normalizePath(e.urlAfterRedirects)),
    distinctUntilChanged(),
    tap(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    })
  );
  protected readonly sessionIsVerified$ = of(
    this.authenticationDomainService.verifySessionRequiresRefresh()
  ).pipe(
    switchMap((requiresRefresh) =>
      requiresRefresh ? this.authenticationDomainService.refresh().pipe(map(() => true)) : of(true)
    ),
    catchError(() => of(true)),
    startWith(false)
  );
  protected readonly updateApp$ = this.swUpdate.versionUpdates.pipe(
    filter(({ type }) => this.swUpdate.isEnabled && type === 'VERSION_READY'),
    switchMap(() => this.matDialog.open(UpdateAvailableComponent).afterClosed()),
    first(),
    map(R.isTruthy),
    tap(() => window.location.reload()),
    takeUntilDestroyed()
  );
  protected readonly vm = Object.freeze({
    sessionIsVerified: toSignal(this.sessionIsVerified$, { requireSync: true }),
  });
  protected readonly autosuscribe = combineLatest([this.updateApp$, this.autoscroll$])
    .pipe(takeUntilDestroyed())
    .subscribe();

  constructor(
    private readonly authenticationDomainService: AuthenticationDomainService,
    private readonly matDialog: MatDialog,
    private readonly router: Router,
    private readonly swUpdate: SwUpdate
  ) {}

  private normalizePath = (url: string): string => {
    const noHashNoQuery = url.split('#')[0].split('?')[0];
    return noHashNoQuery
      .split('/')
      .map((seg) => seg.split(';')[0])
      .join('/');
  };
}
